import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { ConfigStateService, AuthService } from '@abp/ng.core';
import { SommelierService } from '@proxy/sommeliers';
import { ClientService } from '@proxy/clients';
import { Observable, of, switchMap, map, catchError, filter, take } from 'rxjs';

/**
 * Guard que valida que el usuario tenga acceso al slug especificado en la URL.
 * 
 * Lógica de validación:
 * - Si el usuario NO está autenticado -> permite acceso (rutas públicas pueden usar slug)
 * - Si el usuario es ADMIN -> permite acceso (admin puede ver cualquier slug)
 * - Si el usuario es SOMMELIER -> solo permite acceso a su propio slug
 * - Si el usuario es CLIENT -> solo permite acceso al slug de su sommelier asignado
 * - Si el slug no coincide -> redirige a la página home del usuario
 * 
 * Uso:
 * - Aplicar a rutas con :slug que requieren validación de propiedad
 * - Ejemplo: { path: ':slug/orders', component: OrdersComponent, canActivate: [authGuard, slugOwnershipGuard] }
 */
export const slugOwnershipGuard: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean> => {
  const auth = inject(AuthService);
  const config = inject(ConfigStateService);
  const router = inject(Router);
  const sommelierService = inject(SommelierService);
  const clientService = inject(ClientService);

  // Obtener el slug de la URL
  // El slug puede estar en route.params (si es ruta directa) o en route.parent.params (si es ruta anidada)
  const urlSlug = route.params['slug'] || route.parent?.params['slug'] || route.parent?.parent?.params['slug'];
  
  // Si no hay slug en la URL, permitir acceso (no es una ruta con slug)
  if (!urlSlug) {
    return of(true);
  }

  // Si el usuario NO está autenticado, permitir acceso (puede ser ruta pública)
  if (!auth.isAuthenticated) {
    return of(true);
  }

  return config.getOne$('currentUser').pipe(
    filter(user => !!user),
    take(1),
    switchMap(user => {
      const roles = (user as any)?.roles as string[] | undefined;

      // Si no hay roles, esperar a que se carguen
      if (!roles || roles.length === 0) {
        return config.refreshAppState().pipe(
          switchMap(() => config.getOne$('currentUser').pipe(
            filter(u => !!u && Array.isArray((u as any).roles) && (u as any).roles.length > 0),
            take(1)
          ))
        );
      }

      return of(user as any);
    }),
    switchMap(userWithRoles => {
      const roles = (userWithRoles as any)?.roles ?? [];

      // ADMIN puede acceder a cualquier slug
      if (roles.includes('admin')) {
        return of(true);
      }

      // SOMMELIER: validar que el slug sea el propio
      if (roles.includes('sommelier')) {
        return sommelierService.getMySlug().pipe(
          map(mySlug => {
            if (mySlug?.toLowerCase() === urlSlug.toLowerCase()) {
              return true; // Es su propio slug, permitir acceso
            }
            // No es su slug, redirigir a su página home
            router.navigate([`/${mySlug}`]);
            return false;
          }),
          catchError(() => {
            // Error al obtener slug, redirigir a home
            router.navigate(['/']);
            return of(false);
          })
        );
      }

      // CLIENT: validar que el slug sea el de su sommelier asignado
      if (roles.includes('client')) {
        return clientService.getSommelierSlug().pipe(
          map(sommelierSlug => {
            if (sommelierSlug?.toLowerCase() === urlSlug.toLowerCase()) {
              return true; // Es el slug de su sommelier, permitir acceso
            }
            // No es el slug de su sommelier, redirigir a su página home
            router.navigate([`/${sommelierSlug}`]);
            return false;
          }),
          catchError(() => {
            // Error al obtener slug, redirigir a home
            router.navigate(['/']);
            return of(false);
          })
        );
      }

      // Otros roles o sin rol relevante -> permitir acceso (puede ser ruta pública)
      return of(true);
    }),
    catchError(() => {
      // Ante cualquier error, redirigir a home
      router.navigate(['/']);
      return of(false);
    })
  );
};

